import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import SEOManager from "@/components/SEOManager";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import Landing from "@/pages/Landing";
import ComingSoon from "@/pages/ComingSoon";
import WorkspaceGate from "@/pages/WorkspaceGate";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import CompleteProfile from "@/pages/CompleteProfile";
import PostJob from "@/pages/PostJob";
import JobsList from "@/pages/JobsList";
import JobDetail from "@/pages/JobDetail";
import FreelancersList from "@/pages/FreelancersList";
import FreelancerProfile from "@/pages/FreelancerProfile";
import PublicProfile from "@/pages/PublicProfile";
import Dashboard from "@/pages/Dashboard";
import ManageEvent from "@/pages/ManageEvent";
import CaptainDashboard from "@/pages/CaptainDashboard";
import Notifications from "@/pages/Notifications";
import Admin from "@/pages/Admin";
import Messages from "@/pages/Messages";
import Chat from "@/pages/Chat";
import { About, FAQ, Contact, PrivacyPolicy, TermsOfService, CookiePolicy } from "@/pages/Static";
import CookieBanner from "@/components/CookieBanner";
import TodayReminderPopup from "@/components/TodayReminderPopup";
import CheckIn from "@/pages/CheckIn";
import CreateEvent from "@/pages/CreateEvent";
import EventDetail from "@/pages/EventDetail";
import ManageEventDashboard from "@/pages/ManageEventDashboard";
import ExpenseTracker from "@/pages/ExpenseTracker";
import AddRole from "@/pages/AddRole";

function BlockedScreen() {
  return (
    <div className="min-h-[70vh] grid place-items-center px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-red-100 grid place-items-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
        </div>
        <h1 className="font-display text-2xl text-foreground mb-2">Account Disabled</h1>
        <p className="text-muted-foreground mb-6">Your account has been temporarily disabled. Contact us to restore your account.</p>
        <div className="card-flat text-left space-y-3">
          <a href="mailto:earnmate.in@gmail.com" className="flex items-center gap-2 text-sm text-brand hover:underline">
            📧 earnmate.in@gmail.com
          </a>
          <a href="tel:+919061673057" className="flex items-center gap-2 text-sm text-brand hover:underline">
            📞 +91 9061673057
          </a>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const { user } = useAuth();
  if (user?.blocked) {
    return (
      <>
        <Header />
        <main className="min-h-screen"><BlockedScreen /></main>
        <Footer />
      </>
    );
  }
  return <AppRoutes />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster richColors position="top-right" />
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<WorkspaceGate />} />
      <Route path="/*" element={<HeaderedRoutes />} />
    </Routes>
  );
}
function HeaderedRoutes() {
  return (
    <>
      <Header />
      <SEOManager />
      <main className="min-h-[calc(100vh-4rem)]">
        <Routes>
          <Route path="/events" element={<Landing fixedType="event_staffing" />} />
          <Route path="/freelance" element={<ComingSoon />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/checkin" element={<CheckIn />} />
          <Route path="/create-event" element={<CreateEvent />} />
          <Route path="/edit-event/:eventId" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/manage-event/:id" element={<ManageEventDashboard />} />
          <Route path="/manage-event/:id/expenses" element={<ExpenseTracker />} />
          <Route path="/manage-event/:id/add-role" element={<AddRole />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route path="/jobs" element={<JobsList />} />
          <Route path="/job/:id" element={<JobDetail />} />
          <Route path="/freelancers" element={<FreelancersList />} />
          <Route path="/freelancer/:username" element={<FreelancerProfile />} />
          <Route path="/profile/:username" element={<PublicProfile />} />
          <Route path="/manager/:username" element={<PublicProfile />} />
          <Route path="/post-job" element={<ProtectedRoute><PostJob /></ProtectedRoute>} />
          <Route path="/edit-job/:jobId" element={<ProtectedRoute><PostJob /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/manage/:id" element={<ProtectedRoute><ManageEvent /></ProtectedRoute>} />
          <Route path="/captain" element={<ProtectedRoute><CaptainDashboard /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/:partnerId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute role="admin"><Admin /></ProtectedRoute>} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
        </Routes>
      </main>
      <CookieBanner />
      <PWAInstallBanner />
      <TodayReminderPopup />
    </>
  );
}
export default App;